import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Table, Spinner } from 'react-bootstrap';

export function ContentImportModal({ show, onHide, course, onImportSuccess }) {
  const [csvFile, setCsvFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [newSections, setNewSections] = useState(new Set());

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCsvFile(file);
      setParsedData([]);
      setErrors([]);
      setShowPreview(false);
    }
  };

  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) {
      setErrors(['CSV must have at least a header row and one data row']);
      return false;
    }

    // Parse header
    const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase().trim());
    const requiredHeaders = ['section', 'title', 'content'];
    const hasRequired = requiredHeaders.every(h => headers.includes(h));

    if (!hasRequired) {
      setErrors([`CSV must contain these columns: ${requiredHeaders.join(', ')}`]);
      return false;
    }

    // Parse rows
    const parsed = [];
    const foundErrors = [];
    const foundNewSections = new Set();

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue; // Skip empty lines

      const values = parseCSVLine(line);
      const row = {};

      headers.forEach((header, idx) => {
        row[header] = values[idx] || '';
      });

      const validation = validateRow(row, i + 1, course, foundNewSections);
      if (validation.isValid) {
        parsed.push(validation.data);
        if (validation.isNewSection) {
          foundNewSections.add(validation.data.section);
        }
      } else {
        foundErrors.push(...validation.errors);
      }
    }

    if (foundErrors.length > 0) {
      setErrors(foundErrors);
      return false;
    }

    if (parsed.length === 0) {
      setErrors(['No valid content found in CSV']);
      return false;
    }

    setParsedData(parsed);
    setNewSections(foundNewSections);
    setShowPreview(true);
    return true;
  };

  // Helper function to parse CSV line respecting quoted fields
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let insideQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (insideQuotes && nextChar === '"') {
          // Escaped quote
          current += '"';
          i++;
        } else {
          // Toggle quote state
          insideQuotes = !insideQuotes;
        }
      } else if (char === ',' && !insideQuotes) {
        // End of field
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    // Add last field
    result.push(current.trim());
    return result;
  };

  const validateRow = (row, lineNum, course, newSectionsSet) => {
    const errors = [];
    
    // Parse boolean values from CSV
    const parseBoolean = (value) => {
      if (value === undefined || value === null || value === '') return undefined;
      const str = String(value).toLowerCase().trim();
      if (str === 'true' || str === '1' || str === 'yes') return true;
      if (str === 'false' || str === '0' || str === 'no') return false;
      return undefined;
    };
    
    // Helper to get value from row with multiple possible field names
    const getRowValue = (possibleNames) => {
      for (const name of possibleNames) {
        if (
          Object.prototype.hasOwnProperty.call(row, name)
          && row[name] !== undefined
          && row[name] !== null
          && row[name] !== ''
        ) {
          return row[name];
        }
      }
      return '';
    };
    
    const data = {
      section: getRowValue(['section']) || '',
      title: getRowValue(['title']) || '',
      content: getRowValue(['content']) || '',
      contentType: getRowValue(['content_type', 'contenttype', 'content type', 'type']) || 'text',
      showInInitial: parseBoolean(getRowValue(['show_in_initial', 'showinitial', 'show in initial'])) !== false, // Default true
      showInReview: parseBoolean(getRowValue(['show_in_review', 'showinreview', 'show in review'])) === true, // Default false
      tags: getRowValue(['tags']) || '',
    };

    // Validate required fields
    if (!data.section.trim()) {
      errors.push(`Row ${lineNum}: Section is required`);
    }

    if (!data.title.trim()) {
      errors.push(`Row ${lineNum}: Title is required`);
    }

    if (!data.content.trim()) {
      errors.push(`Row ${lineNum}: Content is required`);
    }

    // Validate content type
    const validTypes = ['text', 'html', 'video', 'image'];
    if (data.contentType && !validTypes.includes(data.contentType.toLowerCase())) {
      errors.push(`Row ${lineNum}: Invalid content type "${data.contentType}". Must be one of: ${validTypes.join(', ')}`);
    }

    // Check if section exists
    const sectionExists = course.sections?.some(s => s.title.toLowerCase() === data.section.toLowerCase());
    if (!sectionExists && !newSectionsSet.has(data.section)) {
      newSectionsSet.add(data.section);
    }

    return {
      isValid: errors.length === 0,
      errors,
      data,
      isNewSection: !sectionExists,
    };
  };

  const handleParseClick = () => {
    if (!csvFile) {
      setErrors(['Please select a CSV file']);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        parseCSV(e.target.result);
      } catch (err) {
        setErrors([`Error reading file: ${err.message}`]);
      }
    };
    reader.readAsText(csvFile);
  };

  const handleImportClick = async () => {
    setIsProcessing(true);
    try {
      await onImportSuccess(parsedData);
      handleClose();
    } catch (err) {
      setErrors([`Import failed: ${err.message}`]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setCsvFile(null);
    setParsedData([]);
    setErrors([]);
    setShowPreview(false);
    setNewSections(new Set());
    onHide();
  };

  const handleDownloadSample = () => {
    const link = document.createElement('a');
    link.href = '/assets/sample-content-import.csv';
    link.download = 'sample-content-import.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>📚 Import Content from CSV</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* CSV Format Info */}
        <Alert variant="info" className="mb-3">
          <strong>CSV Format:</strong> The CSV must include these columns:
          <ul className="mb-0 mt-2">
            <li><code>section</code> - Module/section name (creates new if not found)</li>
            <li><code>title</code> - Content block title</li>
            <li><code>content</code> - The content text/HTML/URL</li>
            <li><code>content_type</code> (optional) - Type: text, html, video, or image (default: text)</li>
            <li><code>show_in_initial</code> (optional) - Show on first visit: true/false (default: true)</li>
            <li><code>show_in_review</code> (optional) - Show on revisits: true/false (default: false)</li>
            <li><code>tags</code> (optional) - Tags for the content (semicolon-separated)</li>
          </ul>
          <div className="mt-3 pt-3 border-top">
            <Button 
              variant="outline-info" 
              size="sm"
              onClick={handleDownloadSample}
            >
              📥 Download Sample CSV
            </Button>
          </div>
        </Alert>

        {/* File Input */}
        {!showPreview && (
          <Form.Group className="mb-3">
            <Form.Label>Select CSV File</Form.Label>
            <Form.Control
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              disabled={isProcessing}
            />
          </Form.Group>
        )}

        {/* Errors */}
        {errors.length > 0 && (
          <Alert variant="danger" className="mb-3">
            <strong>Errors found:</strong>
            <ul className="mb-0 mt-2">
              {errors.map((err, idx) => (
                <li key={idx}>{err}</li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Preview Table */}
        {showPreview && parsedData.length > 0 && (
          <div>
            <Alert variant="success" className="mb-3">
              ✅ CSV parsed successfully! Found {parsedData.length} content block(s)
              {newSections.size > 0 && (
                <div className="mt-2">
                  New sections to create: <strong>{Array.from(newSections).join(', ')}</strong>
                </div>
              )}
            </Alert>

            <div className="mb-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>Section</th>
                    <th>Title</th>
                    <th>Type</th>
                    <th>Initial?</th>
                    <th>Review?</th>
                    <th>Content Preview</th>
                  </tr>
                </thead>
                <tbody>
                  {parsedData.map((c, idx) => (
                    <tr key={idx}>
                      <td>{c.section}</td>
                      <td>{c.title}</td>
                      <td><code>{c.contentType}</code></td>
                      <td>
                        <span className={c.showInInitial ? 'text-success' : 'text-muted'}>
                          {c.showInInitial ? '✓' : '✗'}
                        </span>
                      </td>
                      <td>
                        <span className={c.showInReview ? 'text-success' : 'text-muted'}>
                          {c.showInReview ? '✓' : '✗'}
                        </span>
                      </td>
                      <td>{c.content.substring(0, 40)}...</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!showPreview ? (
          <>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleParseClick}
              disabled={!csvFile || isProcessing}
            >
              {isProcessing && <Spinner as="span" animation="border" size="sm" className="me-2" />}
              Parse CSV
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setShowPreview(false)}>
              Back
            </Button>
            <Button
              variant="primary"
              onClick={handleImportClick}
              disabled={isProcessing}
            >
              {isProcessing && <Spinner as="span" animation="border" size="sm" className="me-2" />}
              {isProcessing ? 'Importing...' : 'Import Content'}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
